import { FC, Fragment, useMemo, useState } from 'react';
import styles from './total-equity-chart.module.scss';
import classNames from 'classnames';
import { enumKeyByValue } from '@app/shared/helpers';
import { CaseDto, ForecastDto } from '@app/shared/models/contracts/project-dto';
import { abbreviatedValueFormatter, amountValueFormatterProps } from '@app/shared/formatters';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import { useAppSelector } from '@core/hooks/redux-hooks';
import {
  MAX_COLLAPSED_ITEMS,
  MAX_LABEL_ITEMS,
} from '@app/modules/projects/dashboard/pwerm2-widgets/total-equity/constants';
import SvgPlus from '@app/shared/icons/Plus';
import MinusIcon from '@app/shared/icons/MinusIcon';
import { labelTopValue } from '@app/shared/helpers/labeling-helpers';
import {
  DenominationMultiplier,
  InstrumentType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { useLocale } from '@app/core/hooks/useLocale';
import { selectCapitalStructure } from '@app/core/store/capital-structure-slice-selectors';
import { Pwerm2InstrumentColumn } from './Pwerm2InstrumentColumn';
import { PerYearCapitalStructureModal } from '../../shared/per-year-capital-structure/PerYearCapitalStructureModal';
import {
  excludeNetDebtFromChartStructure,
  transformPwermToChartStructure,
} from '../../shared/per-year-capital-structure/per-year-capital-structure-transformers';
import {
  PerYearCapitalStructureChartInstrument,
  PerYearCapitalStructureChartStructure,
} from '../../shared';
import PerYearCapitalStructureChartInstrumentBlock from '../../shared/per-year-capital-structure/PerYearCapitalStructureChartInstrumentBlock';
import { getTotalEquityChartBlockClasses } from '../../shared/totalEquityChartHelper';
import { PerYearCapitalStructureChartInstrumentStack } from '../../shared/per-year-capital-structure/PerYearCapitalStructureChartInstrumentStack';

const PARENT_CLASSNAME = 'total-equity-chart';

interface TotalEquityChartProps {
  isExpanded: boolean;
  filteredCase?: CaseDto;
  forecastData: ForecastDto[];
  setIsExpanded: (isExpanded: boolean) => void;
}

const Pwerm2TotalEquityChartPwerm: FC<TotalEquityChartProps> = ({
  isExpanded,
  setIsExpanded,
  forecastData,
  filteredCase,
}): JSX.Element => {
  const denomination = useAppSelector((state) => state.project.projectDraft.details.denomination);
  const multiplier = DenominationMultiplier[denomination];
  const investmentDate = useAppSelector((state) => state.project.projectDraft.investmentDate);
  const results = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.dashboard.totalEquity
  );
  const { l } = useLocale();
  const capitalStructure = useAppSelector(selectCapitalStructure);

  const [isTotalEquityChartModalVisible, setIsTotalEquityChartModalVisible] = useState(false);
  const [selectedStructure, setSelectedStructure] =
    useState<PerYearCapitalStructureChartStructure | null>(null);

  const totalInvestments = filteredCase
    ? results.cases[filteredCase.caseId].entryTotalEquity
    : results.weighted.entryTotalEquity;

  const entryResults = filteredCase
    ? results.cases[filteredCase.caseId].chart.entry
    : results.weighted.chart.entry;

  const forecastResults = forecastData.map((f) =>
    filteredCase
      ? results.cases[filteredCase.caseId].chart.years[f.forecastYear]
      : results.weighted.chart.years[f.forecastYear]
  );

  const entryStructure = useMemo(
    () => excludeNetDebtFromChartStructure(transformPwermToChartStructure(entryResults)),
    [entryResults]
  );

  const forecastStructures = useMemo(
    () =>
      forecastResults.map((forecast) =>
        excludeNetDebtFromChartStructure(transformPwermToChartStructure(forecast))
      ),
    [forecastResults]
  );

  const chartForecastsCount = isExpanded
    ? forecastData.length
    : forecastData.length > MAX_COLLAPSED_ITEMS
    ? MAX_COLLAPSED_ITEMS
    : forecastData.length;

  const instrumentBlockRenderer = (
    instrument: PerYearCapitalStructureChartInstrument,
    index: number
  ) => (
    <PerYearCapitalStructureChartInstrumentBlock
      key={'instrument-block-component' + index + instrument.id}
      instrument={instrument}
      className={getTotalEquityChartBlockClasses(instrument.type, instrument.isSweet)}
    />
  );

  const totalEquityPerForecast = filteredCase
    ? Object.values(results.cases[filteredCase.caseId].chart.years).map(
        (x) => x.presentValueOfTotalEquity
      )
    : Object.values(results.weighted.chart.years).map((x) => x.presentValueOfTotalEquity);

  const maxEquity = Math.max(totalInvestments, ...totalEquityPerForecast);

  const calculatedLabelTopValue = labelTopValue(maxEquity);

  const shlInstrumentsExist = Object.values(capitalStructure.instrumentDefinitions).some(
    (i) => i.type === enumKeyByValue(InstrumentType, InstrumentType.ShareholderLoanNotes)
  );

  const prefShareInstrumentsExist = Object.values(capitalStructure.instrumentDefinitions).some(
    (i) => i.type === enumKeyByValue(InstrumentType, InstrumentType.PreferredShares)
  );

  const ordEquityInstrumentsExist = Object.values(capitalStructure.instrumentDefinitions).some(
    (i) =>
      i.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity) && !i.isSweetEquity
  );

  const sweetEquityInstrumentsExist = Object.values(capitalStructure.instrumentDefinitions).some(
    (i) =>
      i.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity) && i.isSweetEquity
  );

  return (
    <>
      <div className={styles[PARENT_CLASSNAME]}>
        <div className={styles[`${PARENT_CLASSNAME}__legend`]}>
          {shlInstrumentsExist && (
            <Button appearance={ButtonAppearance.CLEAN} ignoreReadOnly>
              <span
                className={classNames([
                  styles[`${PARENT_CLASSNAME}__icon`],
                  styles[`${PARENT_CLASSNAME}__icon--sln`],
                ])}
              />
              {InstrumentType.ShareholderLoanNotes}
            </Button>
          )}
          {prefShareInstrumentsExist && (
            <Button appearance={ButtonAppearance.CLEAN} ignoreReadOnly>
              <span
                className={classNames([
                  styles[`${PARENT_CLASSNAME}__icon`],
                  styles[`${PARENT_CLASSNAME}__icon--pref`],
                ])}
              />
              {InstrumentType.PreferredShares}
            </Button>
          )}
          {ordEquityInstrumentsExist && (
            <Button appearance={ButtonAppearance.CLEAN} ignoreReadOnly>
              <span
                className={classNames([
                  styles[`${PARENT_CLASSNAME}__icon`],
                  styles[`${PARENT_CLASSNAME}__icon--ord`],
                ])}
              />
              {InstrumentType.OrdinaryEquity}
            </Button>
          )}
          {sweetEquityInstrumentsExist && (
            <Button appearance={ButtonAppearance.CLEAN} ignoreReadOnly>
              <span
                className={classNames([
                  styles[`${PARENT_CLASSNAME}__icon`],
                  styles[`${PARENT_CLASSNAME}__icon--sweet`],
                ])}
              />
              {l('_SweetEquityTitle')}
            </Button>
          )}
        </div>
        <div className={styles[`${PARENT_CLASSNAME}__chart`]}>
          {[...Array(MAX_LABEL_ITEMS)].map((_, i) => (
            <div
              key={`grid-line-${i}`}
              className={classNames([
                styles[`${PARENT_CLASSNAME}__grid-line`],
                styles[`${PARENT_CLASSNAME}__grid-line--${i + 1}`],
              ])}
              data-grid-value={abbreviatedValueFormatter({
                value: (calculatedLabelTopValue / MAX_LABEL_ITEMS) * (i + 1) * multiplier,
                ...amountValueFormatterProps,
              })}
            />
          ))}
          <div className={styles[`${PARENT_CLASSNAME}__chart-inner`]}>
            <Pwerm2InstrumentColumn
              instrumentStack={
                <PerYearCapitalStructureChartInstrumentStack
                  key="entry-structure-instrument-stack"
                  structure={entryStructure}
                  isExpanded={isExpanded}
                  instrumentBlockRenderer={instrumentBlockRenderer}
                />
              }
              isExpanded={isExpanded}
              heightPct={(100 / calculatedLabelTopValue) * totalInvestments}
              isEntryColumn
              forecastData={entryResults}
              onClick={() => {
                setSelectedStructure(entryStructure);
                setIsTotalEquityChartModalVisible(true);
              }}
            />
            {[...Array(chartForecastsCount)].map((_, i) => (
              <Fragment key={'forecast-wrapper-' + i}>
                <Pwerm2InstrumentColumn
                  instrumentStack={
                    <PerYearCapitalStructureChartInstrumentStack
                      key={`forecast-structure-instrument-stack-${i}`}
                      structure={forecastStructures[i]}
                      isExpanded={isExpanded}
                      instrumentBlockRenderer={instrumentBlockRenderer}
                    />
                  }
                  isExpanded={isExpanded}
                  heightPct={(100 / calculatedLabelTopValue) * totalEquityPerForecast[i]}
                  isLastForecast={i === chartForecastsCount - 1}
                  forecastData={forecastResults[i]}
                  onClick={() => {
                    setSelectedStructure(forecastStructures[i]);
                    setIsTotalEquityChartModalVisible(true);
                  }}
                />
                {i === MAX_COLLAPSED_ITEMS - 1 && forecastData.length > MAX_COLLAPSED_ITEMS && (
                  <div
                    className={classNames(styles['graph__expander-container'], {
                      [styles['graph__expander-container--secondary']]: !isExpanded,
                    })}>
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className={styles['graph__expander']}>
                      {isExpanded ? <MinusIcon /> : <SvgPlus />}
                    </button>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
      <PerYearCapitalStructureModal
        key={selectedStructure?.forecastDate}
        isOpen={isTotalEquityChartModalVisible}
        structure={selectedStructure}
        onClose={() => setIsTotalEquityChartModalVisible(false)}
        isEntryStack={selectedStructure?.forecastDate === investmentDate}
        percentageBasis="netEquity"
      />
    </>
  );
};

export default Pwerm2TotalEquityChartPwerm;
